import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Box,
  Typography,
  Grid,
  CardContent,
  Avatar,
  Chip,
  CircularProgress,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  People as PeopleIcon,
  Assignment as AssignmentIcon,
  CalendarToday as CalendarIcon,
} from '@mui/icons-material';
import HoverCard3D from '../components/HoverCard3D';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export default function Insights() {
  const { user } = useAuth();
  const isStaff = user?.role === 'staff';
  const isClinicAdmin = user?.role === 'clinic_admin';
  const isProviderOnly = user?.role === 'provider';

  const [dashStats, setDashStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [dashboardPeriod, setDashboardPeriod] = useState('today');
  const [refMonth, setRefMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
  const [refYear, setRefYear] = useState(() => new Date().getFullYear());
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');

  useEffect(() => {
    if (!user?.role) return;
    if (isStaff) return;
    if (!isClinicAdmin && !isProviderOnly) return;
    if (dashboardPeriod === 'custom' && (!customStart || !customEnd)) {
      setDashStats(null);
      setStatsLoading(false);
      return;
    }

    const params = new URLSearchParams();
    params.set('period', dashboardPeriod);
    if (dashboardPeriod === 'custom') {
      if (customStart) params.set('start_date', customStart);
      if (customEnd) params.set('end_date', customEnd);
    } else if (dashboardPeriod === 'month' && refMonth) {
      params.set('ref_date', `${refMonth}-01`);
    } else if (dashboardPeriod === 'year') {
      params.set('ref_date', `${refYear}-06-15`);
    }

    const path = isClinicAdmin ? '/api/v1/admin/dashboard/stats' : '/api/v1/clinical/dashboard/stats';
    const url = `${API_URL}${path}?${params.toString()}`;

    setStatsLoading(true);
    axios
      .get(url)
      .then((res) => setDashStats(res.data))
      .catch(() => setDashStats(null))
      .finally(() => setStatsLoading(false));
  }, [
    user?.role,
    isStaff,
    isClinicAdmin,
    isProviderOnly,
    dashboardPeriod,
    refMonth,
    refYear,
    customStart,
    customEnd,
  ]);

  if (isStaff) {
    return (
      <Box sx={{ py: 6, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          Insights are available to providers and clinic admins.
        </Typography>
      </Box>
    );
  }

  const periodScopeLabel =
    dashboardPeriod === 'today'
      ? 'today'
      : dashboardPeriod === 'month'
        ? 'this month'
        : dashboardPeriod === 'year'
          ? 'this year'
          : 'in range';

  const stats = [
    {
      label: `Sessions (${periodScopeLabel})`,
      value: dashStats?.sessions_in_period ?? '—',
      icon: <PeopleIcon />,
      color: '#0d9488',
      hint: 'Sessions started in the selected period',
    },
    {
      label: `Patients seen (${periodScopeLabel})`,
      value: dashStats?.distinct_patients_in_period ?? '—',
      icon: <AssignmentIcon />,
      color: '#047857',
      hint: 'Distinct patients with a session in this period',
    },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Avatar sx={{ bgcolor: '#0d9488', width: 48, height: 48 }}>
          <CalendarIcon />
        </Avatar>
        <Box>
          <Typography variant="h4" fontWeight={700}
            sx={{ fontFamily: '"Source Serif 4", Georgia, serif', color: '#042f2e' }}>
            Insights
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isClinicAdmin
              ? 'Clinic-wide activity for the selected period.'
              : 'Your activity for the selected period.'}
          </Typography>
        </Box>
      </Box>

      {(isClinicAdmin && dashStats?.total_sessions_all_time != null) && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          All-time sessions (clinic): {dashStats.total_sessions_all_time} · Staff & providers:{' '}
          {dashStats.total_providers ?? '—'} / {dashStats.total_staff ?? '—'} · Patients registered:{' '}
          {dashStats.total_patients ?? '—'}
        </Typography>
      )}
      {isProviderOnly && dashStats?.total_sessions_all_time != null && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          All-time sessions (your account): {dashStats.total_sessions_all_time}
        </Typography>
      )}

      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontWeight: 600 }}>
          Reporting period
        </Typography>
        <ToggleButtonGroup
          exclusive
          value={dashboardPeriod}
          onChange={(_, v) => v != null && setDashboardPeriod(v)}
          size="small"
          sx={{ flexWrap: 'wrap', gap: 0.5 }}
        >
          <ToggleButton value="today">Today</ToggleButton>
          <ToggleButton value="month">Month</ToggleButton>
          <ToggleButton value="year">Year</ToggleButton>
          <ToggleButton value="custom">Custom range</ToggleButton>
        </ToggleButtonGroup>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 2, alignItems: 'center' }}>
          {dashboardPeriod === 'month' && (
            <TextField
              type="month"
              label="Month"
              size="small"
              value={refMonth}
              onChange={(e) => setRefMonth(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          )}
          {dashboardPeriod === 'year' && (
            <TextField
              type="number"
              label="Year"
              size="small"
              value={refYear}
              onChange={(e) => setRefYear(Number(e.target.value))}
              inputProps={{ min: 2000, max: 2100 }}
              sx={{ width: 120 }}
            />
          )}
          {dashboardPeriod === 'custom' && (
            <>
              <TextField
                type="date"
                label="From"
                size="small"
                value={customStart}
                onChange={(e) => setCustomStart(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                type="date"
                label="To"
                size="small"
                value={customEnd}
                onChange={(e) => setCustomEnd(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </>
          )}
        </Box>
        {dashStats?.range_start && dashStats?.range_end && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Date range (UTC): {dashStats.range_start} → {dashStats.range_end}
          </Typography>
        )}
      </Box>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        {statsLoading && (
          <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={28} />
          </Grid>
        )}
        {!statsLoading &&
          stats.map((stat) => (
            <Grid item xs={12} sm={6} key={stat.label}>
              <HoverCard3D
                cardSx={{
                  background: 'linear-gradient(160deg, #ffffff 0%, #f0fdfa 100%)',
                  border: '1px solid rgba(13, 148, 136, 0.12)',
                }}
              >
                <CardContent sx={{ display: 'flex', alignItems: 'center', p: 3 }}>
                  <Avatar
                    sx={{
                      bgcolor: `${stat.color}22`,
                      color: stat.color,
                      mr: 2,
                      width: 56,
                      height: 56,
                      boxShadow: `0 10px 24px -8px ${stat.color}66`,
                    }}
                  >
                    {stat.icon}
                  </Avatar>
                  <Box>
                    <Typography variant="h4" fontWeight={800}>
                      {stat.value}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" fontWeight={600}>
                      {stat.label}
                    </Typography>
                    {stat.hint && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        {stat.hint}
                      </Typography>
                    )}
                  </Box>
                </CardContent>
              </HoverCard3D>
            </Grid>
          ))}
      </Grid>

      {isClinicAdmin && Array.isArray(dashStats?.provider_breakdown) && dashStats.provider_breakdown.length > 0 && (
        <HoverCard3D
          cardSx={{
            background: '#ffffff',
            border: '1px solid rgba(4, 47, 46, 0.08)',
            mb: 4,
          }}
        >
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6" fontWeight={700} sx={{ fontFamily: '"Source Serif 4", Georgia, serif' }}>
                Doctors at a glance
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Period: {dashStats?.range_start} → {dashStats?.range_end}
              </Typography>
            </Box>
            <Box sx={{ overflowX: 'auto' }}>
              <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
                <Box component="thead">
                  <Box component="tr" sx={{ '& th': { textAlign: 'left', py: 1, px: 1, borderBottom: '1px solid rgba(4,47,46,0.1)', fontSize: 13, color: 'text.secondary', fontWeight: 700 } }}>
                    <Box component="th">Doctor</Box>
                    <Box component="th">Sessions in period</Box>
                    <Box component="th">Patients in period</Box>
                    <Box component="th">Avg time / patient</Box>
                    <Box component="th">Today: waiting</Box>
                    <Box component="th">Today: done</Box>
                  </Box>
                </Box>
                <Box component="tbody">
                  {dashStats.provider_breakdown.map((row) => {
                    const avg = row.avg_session_seconds;
                    const avgLabel = avg == null
                      ? '—'
                      : avg < 60 ? `${avg}s`
                        : `${Math.floor(avg / 60)}m ${avg % 60}s`;
                    return (
                      <Box key={row.provider_id} component="tr" sx={{ '& td': { py: 1, px: 1, borderBottom: '1px solid rgba(4,47,46,0.05)' } }}>
                        <Box component="td">
                          <Typography fontWeight={600}>{row.provider_name}</Typography>
                          <Typography variant="caption" color="text.secondary">{row.role === 'clinic_admin' ? 'Clinic admin' : 'Provider'}</Typography>
                        </Box>
                        <Box component="td"><Typography fontWeight={700}>{row.sessions_in_period}</Typography></Box>
                        <Box component="td">{row.patients_in_period}</Box>
                        <Box component="td">{avgLabel}</Box>
                        <Box component="td">
                          <Chip size="small" label={row.waiting_today} color={row.waiting_today > 0 ? 'warning' : 'default'} variant="outlined" />
                        </Box>
                        <Box component="td">
                          <Chip size="small" label={row.done_today} color="success" variant="outlined" />
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            </Box>
          </CardContent>
        </HoverCard3D>
      )}
    </Box>
  );
}
